export type LocationPermission = PermissionState;

// navigator.permissions is missing in older Safari and some in-app browsers.
// Treat an unknown state as 'prompt' so the user gets asked.
export const getLocationPermission = async (): Promise<LocationPermission> => {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'prompt';
  }

  try {
    const { state } = await navigator.permissions.query({
      name: 'geolocation',
    });

    return state;
  } catch {
    return 'prompt';
  }
};

// Shows the browser's location prompt and resolves once the user answers.
// Dismissing the prompt counts as denied. A timeout or unavailable position
// still means permission was given. The timeout only starts after the user
// answers, and stops a slow location fix from holding up the check.
export const requestLocationPermission = () =>
  new Promise<Exclude<LocationPermission, 'prompt'>>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve('denied');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (error) =>
        resolve(error.code === error.PERMISSION_DENIED ? 'denied' : 'granted'),
      { maximumAge: 60 * 60 * 1000, timeout: 10 * 1000 },
    );
  });
